import { LIMIT_EXCEEDED, MFA_COMPLETED, MFA_INIT, NO_DEVICE_FOUND } from '@/features/mfa/constants';
import { selectHasDevices } from '@/features/mfaDevices/selectors';
import { selectIsLoggedIn } from '@/features/user/selectors';
import { selectNoUsableDevicesError } from '@/features/user/selectors';

import { createSelector } from '@reduxjs/toolkit';
import { head, prop } from 'ramda';

export const selectMfa = prop('mfa');
export const selectMfaDevicesRoot = prop('mfaDevices');

export const selectFlowId = createSelector(selectMfa, prop('flowId'));
export const selectIsFlowIdLoading = createSelector(selectMfa, prop('isFlowIdLoading'));
export const selectIsFlowIdError = createSelector(selectMfa, prop('isFlowIdError'));
export const selectMfaDevices = createSelector(selectMfaDevicesRoot, prop('devices'));
export const selectMfaDevice = createSelector(selectMfaDevices, head);
export const selectIsMfaSkipped = createSelector(selectMfa, prop('skipped'));
export const selectOtpRequested = createSelector(selectMfa, prop('otpRequested')); // New selector
export const selectIsMfaSignInLoading = createSelector(selectMfa, prop('isMfaSignInLoading'));
export const selectMfaStatus = createSelector(selectMfa, prop('status'));
export const selectHasFetchedDevices = createSelector(
  selectMfaDevicesRoot,
  prop('hasFetchedDevices')
);
export const selectDeviceLimetExceeded = createSelector(
  selectNoUsableDevicesError,
  (error) => error === LIMIT_EXCEEDED
);
export const selectNoDeviceFound = createSelector(
  selectNoUsableDevicesError,
  (error) => error === NO_DEVICE_FOUND
);
export const selectIsMfaCompleted = createSelector(
  selectMfaStatus,
  (mfaStatus) => mfaStatus === 'COMPLETED'
);
export const selectShouldShowOtp = createSelector(
  [selectHasDevices, selectDeviceLimetExceeded, selectIsMfaSkipped, selectIsMfaCompleted],
  (hasDevices, deviceLimitExceeded, isMfaSkipped, isMfaCompleted) => {
    if (isMfaSkipped) return false;

    return (hasDevices || deviceLimitExceeded) && !isMfaCompleted;
  }
);
export const selectShouldShowAddDevice = createSelector(
  [selectHasDevices, selectNoDeviceFound, selectIsMfaSkipped],
  (hasDevices, noDeviceFound, isMfaSkipped) => {
    if (isMfaSkipped) return false;
    return !hasDevices || noDeviceFound;
  }
);
export const selectShouldShowMfaContainer = createSelector(
  [
    selectIsMfaCompleted,
    selectIsLoggedIn,
    selectHasFetchedDevices,
    selectShouldShowOtp,
    selectShouldShowAddDevice
  ],
  (isMfaCompleted, isLoggedIn, hasFetchedDevices, shouldShowOtp, shouldShowAddDevice) => {
    if (isMfaCompleted || !isLoggedIn || !hasFetchedDevices) return false;
    return shouldShowOtp || shouldShowAddDevice;
  }
);

export const selectShouldShowMfaDevicesContainer = createSelector(
  [selectHasDevices, selectMfaStatus, selectIsMfaSkipped],
  (hasDevices, mfaStatus, shouldSkipMfa) =>
    hasDevices && (mfaStatus === MFA_COMPLETED || shouldSkipMfa || mfaStatus === MFA_INIT) // Explicitly handle "INIT" state
);
export const selectShouldShowMfaErrorPage = createSelector(
  [selectIsLoggedIn, selectMfaStatus, selectHasDevices],
  (isLoggedIn, mfaStatus, hasDevices) => isLoggedIn && mfaStatus === 'FAILED' && hasDevices
);
